import React, { useState } from "react";
import { Dialog, Alert } from "./index";
import {
  uiLanguage,
  userClientDb,
  refreshData,
  useExtraField1,
  useExtraField2,
  useExtraField3,
  useExtraField4,
  useExtraField5,
  userDepartmentId,
  userLot2Usage,
  userLotUsage,
  userObject2Usage,
  userObjectUsage,
  userExpDateUsage,
} from "../modules/auth/selectors/userSelector";
import { setUserRefreshData } from "../modules/auth/slices/userSlice";
import { useAppSelector, useAppDispatch } from "../app/hook" ;
import { useTranslation } from "react-i18next";
import { getErrorMessage, getWarningMessage } from "./form/formUtils";
import { Grid2 as Grid } from "@mui/material";
import { useRowAdjustmentsStyles } from "./css";
import { canUserAdjustRowQuantity } from "../modules/api/parameters";
import {
  updateDocDetails,
  IUpdateDocDetails,
} from "../modules/api/docListsandDetails";
import { ItemField } from "./table";
import { useLoader, useAuthorizationError } from "../hooks";
import { isNumber } from "../utils/validate";
import { ExtraFields, OutlinedTextField } from "./form";
import { Form } from "react-final-form";
import { extraFieldsArray } from "../modules/auth/selectors/userSelector";
import * as modules from "../utils/modules";

interface IRowAdjustments {
  open: boolean;
  onClose: () => void;
  rowData: { [key: string]: any };
}

const subscription = {
  submitError: true,
  submitting: true,
  errors: true,
};

function RowAdjustments(props: IRowAdjustments) {
  const { open, onClose, rowData } = props;
  const extraFieldsList = useAppSelector(extraFieldsArray);
  const rowQuantity =
    typeof rowData.quantityApp === "string"
      ? parseFloat(rowData.quantityApp)
      : rowData.quantityApp;
  const [showAlert, setShowAlert] = useState(false);
  const [logoutUser] = useAuthorizationError();
  const [errorMessage, setErrorMessage] = useState({
    message: "",
    error: false,
    status: 0,
  });
  const [, setLoader] = useLoader();
  const [translate] = useTranslation();
  const locale = useAppSelector(uiLanguage);
  const userDb = useAppSelector(userClientDb);
  const { classes } = useRowAdjustmentsStyles();
  const dispatch = useAppDispatch();
  const refresh = useAppSelector(refreshData);
  const quantityRef = React.useRef<HTMLInputElement | null>(null);
  const noteRef = React.useRef<HTMLInputElement | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement | null>(null);
  const departmentId = useAppSelector(userDepartmentId);
  const extra1 = useAppSelector(useExtraField1);
  const extra2 = useAppSelector(useExtraField2);
  const extra3 = useAppSelector(useExtraField3);
  const extra4 = useAppSelector(useExtraField4);
  const extra5 = useAppSelector(useExtraField5);
  const useLot = useAppSelector(userLotUsage);
  const useObject = useAppSelector(userObjectUsage);
  const useLot2 = useAppSelector(userLot2Usage);
  const useObject2 = useAppSelector(userObject2Usage);
  const useExpDate = useAppSelector(userExpDateUsage);
  const [allowQtyAdjustments, setAllowQtyAdjustments] = useState<
    boolean | undefined
  >();
  const isAllowed =
    (rowData.module !== modules.MODULE_SCANNER &&
      rowData.module !== modules.MODULE_PRODUCT &&
      rowData.module !== modules.MODULE_INTERNAL) ||
    (rowData.module === modules.MODULE_INTERNAL &&
      rowData.operationType?.toUpperCase() === "ATR");

  const [scannedData, setScannedData] = useState({
    clientDbId: userDb ? userDb.id : 0,
    locale: locale,
    module: rowData.module || "",
    operationType: rowData.operationType,
    operationCode: rowData.operationCode,
    barcode: rowData.barcode,
    lot: useLot && isAllowed ? rowData.lot : undefined,
    objectCode: useObject && isAllowed ? rowData.objectCode : undefined,
    lot2:
      (useLot2 && rowData.module === modules.MODULE_INTERNAL) ||
      (useLot &&
        rowData.module === modules.MODULE_INTERNAL &&
        rowData.operationType?.toUpperCase() === "PAJ")
        ? rowData?.lot2
        : undefined,
    objectCode2:
      (useObject2 && rowData.module === modules.MODULE_INTERNAL) ||
      (useObject &&
        rowData.module === modules.MODULE_INTERNAL &&
        rowData.operationType?.toUpperCase() === "PAJ")
        ? rowData?.objectCode2
        : undefined,
    expDate:
      useExpDate &&
      (rowData.module === modules.MODULE_INTERNAL ||
        rowData.module === modules.MODULE_PURCHASES)
        ? rowData?.expDate
        : undefined,
    rowNumber: rowData.rowNumber,
    ignoreWarnings: 0,
    quantityApp: rowQuantity,
    note: rowData?.note || "",
    extraInfo1: rowData?.extraInfo1 || "",
    extraInfo2: rowData?.extraInfo2 || "",
    extraInfo3: rowData?.extraInfo3 || "",
    extraInfo4: rowData?.extraInfo4 || "",
    extraInfo5: rowData?.extraInfo5 || "",
    departmentId: departmentId,
    id: rowData.id,
  });

  const initialValues = {
    note: rowData?.note || "",
    quantityApp: rowQuantity,
    extraInfo1:
      typeof rowData.extraInfo1 === "string" &&
      rowData.extraInfo1.toLowerCase() === "false"
        ? false
        : rowData.extraInfo1,
    extraInfo2:
      typeof rowData.extraInfo2 === "string" &&
      rowData.extraInfo2.toLowerCase() === "false"
        ? false
        : rowData.extraInfo2,
    extraInfo3:
      typeof rowData.extraInfo3 === "string" &&
      rowData.extraInfo3.toLowerCase() === "false"
        ? false
        : rowData.extraInfo3,
    extraInfo4:
      typeof rowData.extraInfo4 === "string" &&
      rowData.extraInfo4.toLowerCase() === "false"
        ? false
        : rowData.extraInfo4,
    extraInfo5:
      typeof rowData.extraInfo5 === "string" &&
      rowData.extraInfo5.toLowerCase() === "false"
        ? false
        : rowData.extraInfo5,
  };

  React.useEffect(() => {
    if (allowQtyAdjustments && quantityRef.current) {
      quantityRef.current.focus();
    } else if (noteRef.current) noteRef.current.focus();
  }, [allowQtyAdjustments]);

  const handleKeyPress = (
    event: React.KeyboardEvent<HTMLInputElement> & { target: HTMLInputElement }
  ) => {
    if (event.key === "Enter") {
      const fieldName =
        event.target && event.target.name ? event.target.name : "";

      if (fieldName === "quantityApp" && noteRef.current) {
        noteRef.current.focus();
      }
    }
  };

  const handleFocus = (
    event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    event.target.select();
  };

  React.useEffect(
    () => {
      setLoader(true);
      const params = {
        clientDbId: userDb ? userDb.id : 0,
        locale: locale,
        module: rowData.module || "",
      };

      canUserAdjustRowQuantity(params)
      
        .then(({ data: dataQty }) => {
          setLoader(false);

          if (dataQty && dataQty[0]) {
            if (dataQty[0].value1?.toUpperCase().includes("T")) {
              setAllowQtyAdjustments(true);
            } else setAllowQtyAdjustments(false);
          }
        })
        .catch((reason) => {
          setLoader(false);
          const message = getErrorMessage(reason);
          setErrorMessage({
            error: true,
            message: message.message,
            status: message.status,
          });

          setShowAlert(true);
        });
    }, // eslint-disable-next-line
    []
  );

  const handleCancelAlert = () => {
    logoutUser(errorMessage.status);

    if (errorMessage.status !== 401) {
      setShowAlert(false);
    }
  };

  const updateData = (params: IUpdateDocDetails) => {
    setLoader(true);

    updateDocDetails(params)
      .then(({ data, warnings }) => {
        setLoader(false);
        //Case when data has a warning message
        if (warnings && warnings.length > 0) {
          const warningMessage = getWarningMessage(warnings);

          setErrorMessage({
            message: warningMessage,
            error: false,
            status: 0,
          });

          setShowAlert(true);
          return;
        }
        if (data && data.length > 0) {
          dispatch(setUserRefreshData(!refresh));
        }
      })
      .catch((reason) => {
        setLoader(false);
        const message = getErrorMessage(reason);
        setErrorMessage({
          error: true,
          message: message.message,
          status: message.status,
        });
        setShowAlert(true);
      });
  };

  const handleSubmit = (values: any) => {
    const enteredQty =
      values.quantityApp && typeof values.quantityApp === "string"
        ? parseFloat(values.quantityApp.replace(",", "."))
        : values.quantityApp;

    let opParams = {
      ...scannedData,
      quantityApp: enteredQty - rowQuantity,
      note: values.note || "",
      extraInfo1:
        typeof values.extraInfo1 === "undefined" ? "" : values.extraInfo1,
      extraInfo2:
        typeof values.extraInfo2 === "undefined" ? "" : values.extraInfo2,
      extraInfo3:
        typeof values.extraInfo3 === "undefined" ? "" : values.extraInfo3,
      extraInfo4:
        typeof values.extraInfo4 === "undefined" ? "" : values.extraInfo4,
      extraInfo5:
        typeof values.extraInfo5 === "undefined" ? "" : values.extraInfo5,
    };

    if (values.extraInfo1 && extra1.type.toUpperCase() === "$N")
      opParams = {
        ...opParams,
        extraInfo1:
          typeof values.extraInfo1 === "string"
            ? values.extraInfo1.replace(",", ".")
            : values.extraInfo1,
      };

    if (values.extraInfo2 && extra2.type.toUpperCase() === "$N")
      opParams = {
        ...opParams,
        extraInfo2:
          typeof values.extraInfo2 === "string"
            ? values.extraInfo2.replace(",", ".")
            : values.extraInfo2,
      };

    if (values.extraInfo3 && extra3.type.toUpperCase() === "$N")
      opParams = {
        ...opParams,
        extraInfo3:
          typeof values.extraInfo3 === "string"
            ? values.extraInfo3.replace(",", ".")
            : values.extraInfo3,
      };

    if (values.extraInfo4 && extra4.type.toUpperCase() === "$N")
      opParams = {
        ...opParams,
        extraInfo4:
          typeof values.extraInfo4 === "string"
            ? values.extraInfo4.replace(",", ".")
            : values.extraInfo4,
      };

    if (values.extraInfo5 && extra5.type.toUpperCase() === "$N")
      opParams = {
        ...opParams,
        extraInfo5:
          typeof values.extraInfo5 === "string"
            ? values.extraInfo5.replace(",", ".")
            : values.extraInfo5,
      };

    setScannedData(opParams);
    updateData(opParams);
  };

  const handleConfirmAlert = () => {
    const opParams = {
      ...scannedData,
      ignoreWarnings: 1,
    };
    setShowAlert(false);
    updateData(opParams);
  };

  return (
    <div>
      {showAlert && (
        <Alert
          message={errorMessage.message}
          isOpen={showAlert}
          onClose={handleCancelAlert}
          isConfirmationButton={!errorMessage.error}
          onConfirm={handleConfirmAlert}
          iconColor={!errorMessage.error ? "warning" : "error"}
        />
      )}

      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
        subscription={subscription}
        render={({ handleSubmit }) => (
          <form onSubmit={handleSubmit} method="post">
            <Dialog
              open={open}
              onClose={onClose}
              isConfirmationButton
              onConfirm={handleSubmit}
              confirmButtonRef={buttonRef}
              title={translate("rowAdjustment", "Eilutės koregavimas")}
            >
              <Grid container spacing={1} direction="column">
                <Grid size={12} className={classes.container}>
                  <ItemField data={rowData} disableMenu={true} />
                </Grid>

                { (
                  <Grid size={12}>
                    <OutlinedTextField
                      fullWidth
                      label={translate("quantityapp", "Atrinktas kiekis")}
                      name="quantityApp"
                      ref={quantityRef}
                      onFocus={handleFocus}
                      onKeyDown={handleKeyPress}
                      validate={isNumber}
                      autoComplete="off"
                    />
                  </Grid>
                )}

                <Grid size={12}>
                  <ExtraFields
                    fieldsList={extraFieldsList}
                    buttonRef={buttonRef}
                    noteRef={noteRef}
                  />
                </Grid>
              </Grid>
            </Dialog>
          </form>
        )}
      />
    </div>
  );
}

export default RowAdjustments;
